// src/pages/Staff.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import {
  Staff,
  fetchStaff,
  deleteStaff,
} from "../api/staff";
import StaffTable from "../components/staff/StaffTable";
import StaffModal from "../components/staff/StaffModal";

const StaffPage: React.FC = () => {
  const { businessId } = useAuth();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStaff = async () => {
    if (!businessId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchStaff();
      setStaff(data);
    } catch (err: any) {
      setError(err.message || "Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const handleAddClick = () => {
    setEditingStaff(null);
    setModalOpen(true);
  };

  const handleEditClick = (s: Staff) => {
    setEditingStaff(s);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingStaff(null);
  };

  const handleStaffSaved = (saved: Staff) => {
    setStaff((prev) => {
      const existingIndex = prev.findIndex(
        (s) => s.staff_id === saved.staff_id
      );
      if (existingIndex === -1) {
        return [...prev, saved];
      } else {
        const copy = [...prev];
        copy[existingIndex] = saved;
        return copy;
      }
    });
  };

  const handleDelete = async (s: Staff) => {
    if (!window.confirm(`Delete staff member "${s.full_name}"?`)) return;

    try {
      await deleteStaff(s.staff_id);
      setStaff((prev) => prev.filter((x) => x.staff_id !== s.staff_id));
    } catch (err: any) {
      alert(err.message || "Failed to delete staff member");
    }
  };

  if (!businessId) {
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mt: 4, textAlign: "center" }}
      >
        Business not found. Please log in again.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1100px", mx: "auto", p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" component="h1">
          Staff
        </Typography>
        <Button variant="contained" onClick={handleAddClick}>
          + Add Staff
        </Button>
      </Stack>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <StaffTable staff={staff} onEdit={handleEditClick} onDelete={handleDelete} />

      <StaffModal
        open={modalOpen}
        staff={editingStaff}
        onClose={handleModalClose}
        onSaved={handleStaffSaved}
      />
    </Box>
  );
};

export default StaffPage;
